import { Link } from "react-router-dom";
import { Check, X, Minus, ArrowLeft } from "lucide-react";

// ── Cell renderers ────────────────────────────────────────────────────────────
function Yes({ label }) {
    return (
        <span className="inline-flex items-center gap-1.5 text-green-700 font-medium text-sm">
            <Check className="w-4 h-4 shrink-0 text-green-600" />
            {label || "Yes"}
        </span>
    );
}
function No({ label }) {
    return (
        <span className="inline-flex items-center gap-1.5 text-tca-mute text-sm">
            <X className="w-4 h-4 shrink-0 text-red-400" />
            {label || "No"}
        </span>
    );
}
function Partial({ label }) {
    return (
        <span className="inline-flex items-center gap-1.5 text-amber-700 text-sm">
            <Minus className="w-4 h-4 shrink-0 text-amber-500" />
            {label || "Partial"}
        </span>
    );
}
function Info({ label }) {
    return <span className="text-tca-ink text-sm leading-snug">{label}</span>;
}

// ── Comparison data ───────────────────────────────────────────────────────────
const CATEGORIES = [
    {
        label: "Getting Started",
        rows: [
            {
                feature: "Cloud-based (no server or local install required)",
                xero: <Yes />,
                sage: <Yes />,
                qb: <Yes />,
            },
            {
                feature: "Free trial period",
                xero: <Info label="30 days" />,
                sage: <Info label="30 days" />,
                qb: <Info label="30 days" />,
            },
            {
                feature: "Unlimited users on every plan (no per-seat fees)",
                xero: <Yes />,
                sage: <No label="Limited by plan tier" />,
                qb: <No label="1–25 users by plan tier" />,
            },
        ],
    },
    {
        label: "Core Accounting",
        rows: [
            {
                feature: "Double-entry accounting engine",
                xero: <Yes />,
                sage: <Yes />,
                qb: <Yes />,
            },
            {
                feature: "Chart of accounts (customisable)",
                xero: <Yes />,
                sage: <Yes />,
                qb: <Yes />,
            },
            {
                feature: "Manual journal entries",
                xero: <Yes />,
                sage: <Yes />,
                qb: <Yes />,
            },
            {
                feature: "Multi-currency support",
                xero: <Yes label="Mid+ plans" />,
                sage: <Yes label="Higher plans" />,
                qb: <Yes label="Mid+ plans" />,
            },
            {
                feature: "Tracking categories / cost centres",
                xero: <Yes label="2 per transaction" />,
                sage: <Yes />,
                qb: <Yes label="Via Classes & Locations" />,
            },
            {
                feature: "Fixed asset register & depreciation",
                xero: <Yes />,
                sage: <Yes />,
                qb: <Partial label="Add-on required" />,
            },
            {
                feature: "Multi-company management",
                xero: <Info label="Separate subscriptions, easy switch" />,
                sage: <Info label="Separate subscriptions" />,
                qb: <Info label="Separate subscriptions" />,
            },
        ],
    },
    {
        label: "Invoicing & Billing",
        rows: [
            {
                feature: "Unlimited invoices",
                xero: <Yes />,
                sage: <Yes />,
                qb: <Partial label="Limited on entry plan" />,
            },
            {
                feature: "Quote and estimate creation",
                xero: <Yes />,
                sage: <Yes />,
                qb: <Yes />,
            },
            {
                feature: "Recurring / repeat invoices",
                xero: <Yes />,
                sage: <Yes />,
                qb: <Yes />,
            },
            {
                feature: "Automated payment reminders",
                xero: <Yes />,
                sage: <Yes />,
                qb: <Yes />,
            },
            {
                feature: "Online payment links on invoices",
                xero: <Yes />,
                sage: <Yes />,
                qb: <Yes />,
            },
        ],
    },
    {
        label: "Banking & Reconciliation",
        rows: [
            {
                feature: "Live bank feeds — all major SA banks (FNB, ABSA, Standard, Nedbank, Capitec, Investec, Discovery)",
                xero: <Yes label="All major SA banks" />,
                sage: <Yes label="Most SA banks" />,
                qb: <Yes label="Most SA banks" />,
            },
            {
                feature: "Automated bank rules & transaction categorisation",
                xero: <Yes />,
                sage: <Yes />,
                qb: <Yes />,
            },
            {
                feature: "Automated bank reconciliation",
                xero: <Yes />,
                sage: <Yes />,
                qb: <Yes />,
            },
            {
                feature: "Find & Recode — bulk transaction recoding tool (up to 9 search conditions, recode 4 fields simultaneously)",
                xero: <Yes label="Full feature — unique to Xero" />,
                sage: <No />,
                qb: <Partial label="Basic bulk edit only — far less powerful" />,
            },
            {
                feature: "Hubdoc document capture & auto-coding (included free)",
                xero: <Yes label="Included on all plans" />,
                sage: <No label="Requires 3rd-party add-on" />,
                qb: <No label="Requires 3rd-party add-on" />,
            },
        ],
    },
    {
        label: "Tax & SARS Compliance",
        rows: [
            {
                feature: "Direct VAT201 eFiling to SARS from within the software",
                xero: <Yes label="Submit directly to SARS eFiling" />,
                sage: <No label="Manual export & re-keying required" />,
                qb: <No label="Manual export & re-keying required" />,
            },
            {
                feature: "VAT return auto-population from transactions",
                xero: <Yes label="Auto-populated, review before submit" />,
                sage: <Partial label="Manual review & preparation" />,
                qb: <Partial label="Manual review & preparation" />,
            },
            {
                feature: "EMP201 / payroll reconciliation reporting",
                xero: <Yes />,
                sage: <Yes />,
                qb: <Partial label="Limited SA-specific support" />,
            },
            {
                feature: "IT14 income tax return mapping",
                xero: <Yes />,
                sage: <Yes />,
                qb: <No label="Not documented for SA" />,
            },
            {
                feature: "Full audit trail (every transaction change logged)",
                xero: <Yes />,
                sage: <Yes />,
                qb: <Yes />,
            },
        ],
    },
    {
        label: "Reporting & Analytics",
        rows: [
            {
                feature: "Profit & loss statement",
                xero: <Yes />,
                sage: <Yes />,
                qb: <Yes />,
            },
            {
                feature: "Balance sheet",
                xero: <Yes />,
                sage: <Yes />,
                qb: <Yes />,
            },
            {
                feature: "Cash flow statement",
                xero: <Yes />,
                sage: <Yes />,
                qb: <Yes />,
            },
            {
                feature: "Aged debtors & creditors reports",
                xero: <Yes />,
                sage: <Yes />,
                qb: <Yes />,
            },
            {
                feature: "Fully customisable financial reports",
                xero: <Yes />,
                sage: <Partial label="Limited customisation" />,
                qb: <Partial label="Limited customisation" />,
            },
            {
                feature: "Budget manager",
                xero: <Yes />,
                sage: <Yes />,
                qb: <Partial label="Higher plans only" />,
            },
            {
                feature: "AI-powered analytics with KPI dashboards (built-in via Syft — acquired by Xero)",
                xero: <Yes label="Analytics Plus — included" />,
                sage: <No />,
                qb: <No />,
            },
            {
                feature: "180-day AI cash flow forecast with scenario modelling",
                xero: <Yes label="Analytics Plus — unique feature" />,
                sage: <No />,
                qb: <No />,
            },
        ],
    },
    {
        label: "Payroll & HR",
        rows: [
            {
                feature: "Built-in SA payroll",
                xero: <Partial label="Add-on required (SimplePay, PaySpace)" />,
                sage: <Yes label="Built-in Sage Payroll" />,
                qb: <Partial label="Basic built-in, limited SA-specific" />,
            },
            {
                feature: "SA payroll software integrations",
                xero: <Yes label="SimplePay, PaySpace, BambooHR & more" />,
                sage: <Yes label="Sage Payroll built-in + partners" />,
                qb: <Partial label="Limited SA-specific options" />,
            },
            {
                feature: "Leave & HR management",
                xero: <Partial label="Via payroll add-on" />,
                sage: <Yes label="Built-in" />,
                qb: <Partial label="Via add-on" />,
            },
        ],
    },
    {
        label: "Inventory & Projects",
        rows: [
            {
                feature: "Inventory / stock management",
                xero: <Partial label="Basic tracked items — advanced via Cin7 / Unleashed" />,
                sage: <Yes label="Advanced native inventory" />,
                qb: <Partial label="Moderate — varies by plan" />,
            },
            {
                feature: "Bill of materials",
                xero: <Partial label="Via Cin7 / Unleashed App Store" />,
                sage: <Yes />,
                qb: <Partial label="Via add-on" />,
            },
            {
                feature: "Project / job costing",
                xero: <Yes label="Built-in basic; advanced via Projects add-on" />,
                sage: <Yes label="Advanced native project accounting" />,
                qb: <Yes label="Higher plans only" />,
            },
            {
                feature: "Time tracking",
                xero: <Partial label="Basic — advanced via add-ons" />,
                sage: <Yes />,
                qb: <Yes label="Higher plans only" />,
            },
        ],
    },
    {
        label: "App Store & Integrations",
        rows: [
            {
                feature: "Dedicated app marketplace size",
                xero: <Info label="1,000+ certified apps" />,
                sage: <Info label="~200 apps" />,
                qb: <Info label="~750 apps" />,
            },
            {
                feature: "Shopify & WooCommerce e-commerce integration",
                xero: <Yes />,
                sage: <Yes />,
                qb: <Yes />,
            },
            {
                feature: "Open & well-documented API for custom integrations",
                xero: <Yes label="Fully open API" />,
                sage: <Yes />,
                qb: <Yes />,
            },
            {
                feature: "SA payment integrations (Yoco, PayStack, Peach Payments)",
                xero: <Yes />,
                sage: <Partial label="Yoco integration available" />,
                qb: <Partial label="Limited SA-specific options" />,
            },
        ],
    },
    {
        label: "Security & Support",
        rows: [
            {
                feature: "Two-factor authentication (2FA)",
                xero: <Yes />,
                sage: <Yes />,
                qb: <Yes />,
            },
            {
                feature: "ISO 27001-certified data security",
                xero: <Yes />,
                sage: <Yes />,
                qb: <Yes />,
            },
            {
                feature: "Granular role-based user permissions",
                xero: <Yes />,
                sage: <Yes />,
                qb: <Yes />,
            },
            {
                feature: "Dedicated South African support team",
                xero: <Yes label="Dedicated SA team" />,
                sage: <Partial label="Regional support" />,
                qb: <No label="No dedicated SA support" />,
            },
        ],
    },
    {
        label: "Unique Differentiators",
        rows: [
            {
                feature: "Certified advisor / Gold Partner network in South Africa",
                xero: <Yes label="Xero Gold & Platinum Partner ecosystem" />,
                sage: <Partial label="Certified partners available" />,
                qb: <No label="Minimal SA-specific advisor network" />,
            },
            {
                feature: "Mobile app (iOS + Android) with full functionality",
                xero: <Yes />,
                sage: <Partial label="Core features only" />,
                qb: <Yes />,
            },
        ],
    },
];

export default function XeroComparison() {
    // Count all rows for display
    const totalFeatures = CATEGORIES.reduce((sum, c) => sum + c.rows.length, 0);

    return (
        <div className="bg-tca-fog min-h-screen" data-testid="page-xero-comparison">

            {/* Top bar */}
            <div className="bg-white border-b border-tca-line sticky top-[110px] z-20 print-hide">
                <div className="container-tca py-4 flex items-center justify-between gap-3 flex-wrap">
                    <Link
                        to="/software"
                        data-testid="xero-comparison-back"
                        className="btn-ghost text-base gap-2.5"
                    >
                        <ArrowLeft className="w-5 h-5" /> Back to Software
                    </Link>
                    <Link
                        to="/contact"
                        data-testid="xero-comparison-migrate-cta"
                        className="btn-primary gap-2"
                    >
                        Ready to migrate to Xero?
                    </Link>
                </div>
            </div>

            {/* Hero */}
            <section className="bg-white border-b border-tca-line">
                <div className="container-tca py-14 md:py-18">
                    <p className="overline text-tca-orange mb-4">Competitive Analysis · South Africa</p>
                    <h1 className="font-display font-extrabold text-tca-ink text-4xl sm:text-5xl lg:text-6xl leading-[1.02] max-w-4xl">
                        Xero vs Sage vs QuickBooks
                    </h1>
                    <p className="mt-5 text-tca-mute text-lg leading-relaxed max-w-3xl">
                        A comprehensive, feature-by-feature comparison of the three most widely used cloud accounting platforms in South Africa — researched for accuracy as of 2026.
                        <span className="block mt-2 text-base">
                            {totalFeatures} features across 11 categories. Xero columns are highlighted where it leads.
                        </span>
                    </p>
                </div>
            </section>

            {/* Table */}
            <section className="bg-white">
                <div className="container-tca py-10 md:py-14">
                    <div className="overflow-x-auto border border-tca-line">
                        <table className="w-full min-w-[700px] border-collapse text-left" data-testid="xero-comparison-table">
                            {/* Sticky column headers */}
                            <thead>
                                <tr className="bg-tca-ink text-white">
                                    <th className="px-5 py-4 font-display font-bold text-sm w-[40%] text-white/70 tracking-wide">
                                        Feature
                                    </th>
                                    <th className="px-5 py-4 font-display font-bold text-sm w-[20%] text-tca-orange tracking-wide border-l border-white/10">
                                        Xero
                                    </th>
                                    <th className="px-5 py-4 font-display font-bold text-sm w-[20%] text-white/80 tracking-wide border-l border-white/10">
                                        Sage
                                    </th>
                                    <th className="px-5 py-4 font-display font-bold text-sm w-[20%] text-white/80 tracking-wide border-l border-white/10">
                                        QuickBooks
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {CATEGORIES.map((cat, ci) => (
                                    <>
                                        {/* Category separator */}
                                        <tr key={`cat-${ci}`} className="bg-tca-fog">
                                            <td
                                                colSpan={4}
                                                className="px-5 py-2.5 text-[11px] font-bold uppercase tracking-[0.2em] text-tca-orange border-t border-b border-tca-line"
                                            >
                                                {cat.label}
                                            </td>
                                        </tr>
                                        {cat.rows.map((row, ri) => (
                                            <tr
                                                key={`row-${ci}-${ri}`}
                                                className={`border-b border-tca-line ${ri % 2 === 0 ? "bg-white" : "bg-tca-fog/40"} hover:bg-tca-orangeSoft/20 transition-colors`}
                                            >
                                                <td className="px-5 py-3.5 text-sm text-tca-ink font-medium leading-snug align-top">
                                                    {row.feature}
                                                </td>
                                                <td className="px-5 py-3.5 align-top border-l border-tca-line bg-orange-50/30">
                                                    {row.xero}
                                                </td>
                                                <td className="px-5 py-3.5 align-top border-l border-tca-line">
                                                    {row.sage}
                                                </td>
                                                <td className="px-5 py-3.5 align-top border-l border-tca-line">
                                                    {row.qb}
                                                </td>
                                            </tr>
                                        ))}
                                    </>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    <p className="mt-6 text-xs text-tca-mute leading-relaxed max-w-3xl italic">
                        Accuracy note: Feature data is researched from official vendor documentation and independent SA accounting software reviews (odea.co.za, readyaccounting.co.za, smartbookie.co.za) as of early 2026. Software features evolve — verify current capabilities on each vendor's official website before making a purchasing decision. "Sage" refers to Sage Business Cloud Accounting (the cloud SME product), not Sage 50 desktop or Sage Intacct.
                    </p>
                </div>
            </section>

            {/* Bottom CTA */}
            <section className="bg-tca-ink text-white">
                <div className="container-tca py-16 md:py-20 grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
                    <div>
                        <h2 className="font-display font-extrabold text-white text-3xl sm:text-4xl leading-[1.1]">
                            Ready to make the move to Xero?
                        </h2>
                        <p className="mt-4 text-white/70 leading-relaxed">
                            TC Auditors handles the full migration — trial balance import, master file setup, bank feed activation and three years of comparatives. Typically done within 2–4 weeks.
                        </p>
                    </div>
                    <div className="flex flex-col sm:flex-row gap-3">
                        <Link
                            to="/contact"
                            data-testid="xero-comparison-bottom-cta"
                            className="btn-primary gap-2"
                        >
                            Plan my Xero migration
                        </Link>
                        <Link
                            to="/software"
                            className="btn-secondary gap-2"
                        >
                            Back to Software & Apps
                        </Link>
                    </div>
                </div>
            </section>
        </div>
    );
}
